import { useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { commitLocalUpdate, ConnectionHandler, useRelayEnvironment } from 'react-relay';
import { type BoundActions, createActionsHook, createHook, createStore, type HookActionsFunction, type HookFunction, type StoreActionApi } from 'react-sweet-state';

import { useRovoEntitlement } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { AgentAction } from '@atlassian/conversation-assistant-service-api/types/agent-actions';
import {
	type Agent,
	type AgentAvailablePlugins,
	type AgentDetails,
	type AgentKnowledgeSource,
	type ExternalFile,
	isRateLimitedError,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import RelayDataID from '@atlassian/relay-data-id';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private.ts';
import { type BrowseCategory } from '../../common/types.ts';
import {
	DEFAULT_PAGE_INFO,
	fetchAgentByExternalConfigReference,
	fetchPaginatedAgents,
	type PageInfo,
} from '../../services/paginated-agents/index.ts';
import { getSuggestedOotbAgents } from '../../utils/agents/index.tsx';
import { useDeleteAgent } from '../agent-delete/index.tsx';
import { useAgentFavourites } from '../agent-favourite/index.tsx';
import { useAgentOverrider } from '../agent-overrider/index.tsx';
import { getOverridenAgents } from '../agent-overrider/utils.ts';

import { isHipaaError } from './constants.ts';
import messages from './messages.ts';

type CategorisedAgents = {
	public: Agent[];
	private: Agent[];
	system: Agent[];
};

const categoriseAgents = (agents: Agent[]): CategorisedAgents =>
	agents.reduce(
		(categorisedAgents: CategorisedAgents, agent: Agent) => {
			if (agent.creator_type === 'SYSTEM') {
				categorisedAgents.system.push(agent);
			} else if (agent.visibility === 'PUBLIC') {
				categorisedAgents.public.push(agent);
			} else if (agent.visibility === 'PRIVATE') {
				categorisedAgents.private.push(agent);
			} else if (!agent.visibility && agent.creator_type === 'CUSTOMER') {
				categorisedAgents.private.push(agent);
			}

			return categorisedAgents;
		},
		{
			public: [],
			private: [],
			system: [],
		},
	);

/**
 * @deprecated Please use `useAgentsPaginated...` hooks instead
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
const agentsStore = createStore({
	initialState: {
		availableAgents: [] as Agent[],
		categorisedAgents: {
			private: [],
			public: [],
			system: [],
		} as CategorisedAgents,
		agentsInitialized: false,
		agentsError: undefined as ApiError | undefined,
	},
	name: 'AgentsStore',
	actions: {
		setAgents:
			(availableAgents: Agent[]) =>
			({ setState }) => {
				setState({
					availableAgents,
					categorisedAgents: categoriseAgents(availableAgents),
				});
			},

		setAgentsInitialized:
			() =>
			({ setState }) => {
				setState({
					agentsInitialized: true,
				});
			},

		setAgentsError:
			(agentsError: ApiError) =>
			({ setState }) => {
				setState({ agentsError });
			},
	},
});

/**
 * @deprecated Please use `useAgentsPaginated...` instead
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
const useAgentsStore = createHook(agentsStore);

type UseAgentsListProps = {
	filter?: 'favourites';
} & AssistanceServiceInjectedParams;

type UseAgentsControllerProps = {
	onError?: (error: ApiError) => void;
} & AssistanceServiceInjectedParams;

/**
 * @deprecated TODO remove as part of pagination work https://product-fabric.atlassian.net/browse/RAGE-60, please use `useAgentsPaginatedController` instead
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
export const useAgentsControllerInternal: (props: Omit<UseAgentsControllerProps, "assistanceServiceInjectedParams"> & AssistanceServiceInputParam) => {
    availableAgents: Agent[];
    favouritedAgents: Agent[];
    categorisedAgents: CategorisedAgents;
    fetchAndStoreAvailableAgents: {
        data: void | null;
        error: ApiError | null;
        isFetching: boolean;
        refetch: () => Promise<void | null>;
    };
    deleteAgent: {
        data: void | null;
        error: ApiError | null;
        isSubmitting: boolean;
        mutate: (payload: string, callbacks?: {
            onSuccess?: ((data: void) => void) | undefined;
            onError?: (error: ApiError) => void;
        } | undefined) => Promise<void | undefined>;
    };
    setFavouriteAgent: (id: string, isFavourite: boolean) => Promise<void | undefined>;
    defaultAgent: Agent | undefined;
    agentsInitialized: boolean;
    nonDefaultAgents: Agent[];
    nonDefaultFavouriteAgents: Agent[];
    updateAgentLocally: (agentId: string, updatedAgent: Partial<Agent>) => void;
    agentsError: ApiError | undefined;
} = createHookWithAssistanceService(
	({
		onError,
		assistanceServiceInjectedParams: {
			assistanceService,
			experienceTracker,
			experienceId,
			product,
			siteId,
			touchpointSource,
		},
	}: UseAgentsControllerProps) => {
		// TODO remove `isRovoEnabled`
		const { isRovoEnabled } = useRovoEntitlement();

		const { agentIdFiltersOverride, agentDefaultIdOverride } = useAgentOverrider();
		const [
			{ availableAgents, categorisedAgents, agentsInitialized, agentsError },
			{ setAgents, setAgentsInitialized, setAgentsError },
		] = useAgentsStore();
		const { formatMessage } = useIntl();

		const { favouriteAgent, unfavouriteAgent } = useAgentFavourites({
			assistanceServiceParams: {
				experienceId,
				product,
				siteId,
				touchpointSource,
			},
		});

		const favouritedAgents = availableAgents.filter((agent) => agent.favourite);
		const defaultAgent = useMemo(
			() => availableAgents.find((agent) => agent.is_default),
			[availableAgents],
		);
		const nonDefaultAgents = useMemo(
			() => availableAgents.filter((agent) => !agent.is_default),
			[availableAgents],
		);
		const nonDefaultFavouriteAgents = useMemo(
			() => nonDefaultAgents.filter((agent) => !agent.favourite),
			[nonDefaultAgents],
		);

		const fetchAndStoreAvailableAgents = useQuery({
			queryKey: ['fetchAgents'],
			queryFn: async () => {
				try {
					experienceTracker.start({
						id: ExperienceName.FETCH_AGENTS,
						name: ExperienceName.FETCH_AGENTS,
					});

					const agents = await assistanceService.getAgents();

					const transformedAgents = agents.map((agent) => {
						// Rename default agent to Chat, which is displayed in the UI
						if (agent.is_default) {
							agent.name = formatMessage(messages.defaultAgentName);
							agent.description = formatMessage(messages.defaultAgentDescription);
						}

						return agent;
					});

					const overriddenAgents = getOverridenAgents(
						transformedAgents,
						agentIdFiltersOverride,
						agentDefaultIdOverride,
					);

					setAgents(overriddenAgents);

					experienceTracker.succeed({
						name: ExperienceName.FETCH_AGENTS,
					});

					setAgentsInitialized();
				} catch (error: any) {
					//Filter out HIPAA errors from experience tracker https://product-fabric.atlassian.net/browse/ROVOFE-543
					if (isHipaaError(error)) {
						experienceTracker.succeed({
							name: ExperienceName.FETCH_AGENTS,
						});
						return;
					}

					if (isRateLimitedError(error)) {
						setAgentsError(error);
					}

					experienceTracker.fail({ name: ExperienceName.FETCH_AGENTS, error });
					throw error;
				}
			},
			onError,
			enabled: isRovoEnabled,
			refetchOnWindowFocus: false,
		});

		const { deleteAgent } = useDeleteAgent({
			onSuccess: () => {
				fetchAndStoreAvailableAgents.refetch();
			},
			assistanceServiceParams: {
				experienceId,
				product,
				siteId,
				touchpointSource,
			},
			onInit: (id) => {
				setAgents(availableAgents.filter((agent) => agent.id !== id));
			},
			onError: () => {
				setAgents(availableAgents);
			},
		});

		const setFavouriteAgent = useCallback(
			async (id: string, isFavourite: boolean) => {
				const updatedAgent = availableAgents.find((a) => a.id === id);
				const setUpdatedAgent = (shouldBeFavourite: boolean) => {
					if (updatedAgent) {
						updatedAgent.favourite = shouldBeFavourite;
						setAgents(
							availableAgents.map((a) => {
								// If the current agent's id matches the new agent's id, return the new agent.
								// Otherwise, return the current agent.
								return a.id === updatedAgent.id ? updatedAgent : a;
							}),
						);
					}
				};

				//Optimistically update favourite value before we mutate
				setUpdatedAgent(isFavourite);

				//Pass callback to roll back optimistic update if mutation fails
				const errorCallback = {
					onError: () => {
						setUpdatedAgent(!isFavourite);
					},
				};
				if (isFavourite) {
					return await favouriteAgent.mutate(id, errorCallback);
				} else {
					return await unfavouriteAgent.mutate(id, errorCallback);
				}
			},
			[availableAgents, setAgents, favouriteAgent, unfavouriteAgent],
		);
		/** This updates the agent on the client side only
		 * Useful for updating the agent after a successful mutation
		 */
		const updateAgentLocally = useCallback(
			(agentId: string, updatedAgent: Partial<Agent>) => {
				const updatedAgents = availableAgents.map((agent) => {
					if (agent.id === agentId) {
						return {
							...agent,
							...updatedAgent,
						};
					}
					return agent;
				});

				setAgents(updatedAgents);
			},
			[availableAgents, setAgents],
		);

		return {
			availableAgents,
			favouritedAgents,
			categorisedAgents,
			fetchAndStoreAvailableAgents,
			deleteAgent,
			setFavouriteAgent,
			defaultAgent,
			agentsInitialized,
			nonDefaultAgents,
			nonDefaultFavouriteAgents,
			updateAgentLocally,
			agentsError,
		};
	},
);

type StoreAgentCategory = {
	agentIds: string[];
	pageInfo: PageInfo;
	abortController: AbortController;
	agentsError?: Error | undefined;
};
type StoreAgentCategories = Record<BrowseCategory, StoreAgentCategory>;

const agentsPaginatedStore = createStore({
	initialState: {
		/*
		Hash map which is the source of truth for agent data
		agent categories simply hold the agent ids to perform a lookup
		*/
		agentsMap: {} as Record<string, Agent>,
		loadingCategories: new Set<BrowseCategory>(),
		agentCategories: {
			['all-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['starred-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['my-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['templates']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['verified-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
		} as StoreAgentCategories,
	},
	name: 'AgentsPaginatedStore',
	actions: {
		/**
		 * This action populates a specific category with backend data
		 * it will store the ids in `agentCategories` and the agents in `agentsMap`
		 */
		populateAgentCategories:
			({
				agentsList,
				pageInfo,
				category = 'all-agents',
			}: {
				agentsList: Agent[];
				pageInfo: PageInfo;
				category: BrowseCategory;
			}): ({ setState, getState }: StoreActionApi<{
                    /*
                    Hash map which is the source of truth for agent data
                    agent categories simply hold the agent ids to perform a lookup
                    */
                    agentsMap: Record<string, Agent>;
                    loadingCategories: Set<BrowseCategory>;
                    agentCategories: StoreAgentCategories;
                }>) => void =>
			({ setState, getState }) => {
				const { agentsMap, agentCategories } = getState();
				const updatedAgentsMap = { ...agentsMap };
				const updatedAgentCategories = { ...agentCategories };

				updatedAgentCategories[category].pageInfo = pageInfo;

				if (!agentsList.length) {
					updatedAgentCategories[category].agentIds = [];
				} else {
					agentsList.forEach((agent) => {
						updatedAgentsMap[agent.id] = agent;
						updatedAgentCategories[category].agentIds.push(agent.id);
					});
				}

				setState({
					agentsMap: updatedAgentsMap,
					agentCategories: updatedAgentCategories,
				});
			},

		setAgentCategoriesByCategory:
			({
				categoryName,
				agentCategoryData,
			}: {
				categoryName: BrowseCategory;
				agentCategoryData: StoreAgentCategory;
			}): ({ getState, setState }: StoreActionApi<{
                    /*
                    Hash map which is the source of truth for agent data
                    agent categories simply hold the agent ids to perform a lookup
                    */
                    agentsMap: Record<string, Agent>;
                    loadingCategories: Set<BrowseCategory>;
                    agentCategories: StoreAgentCategories;
                }>) => void =>
			({ getState, setState }) => {
				const { agentCategories } = getState();

				const updatedAgentCategories = {
					...agentCategories,
					[categoryName]: agentCategoryData,
				};

				setState({ agentCategories: updatedAgentCategories });
			},

		setCategoryError:
			(categoryName: BrowseCategory, agentsError: Error | undefined): ({ getState, setState }: StoreActionApi<{
                /*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
                agentsMap: Record<string, Agent>;
                loadingCategories: Set<BrowseCategory>;
                agentCategories: StoreAgentCategories;
            }>) => void =>
			({ getState, setState }) => {
				const { agentCategories } = getState();
				const updatedAgentCategories = {
					...agentCategories,
					[categoryName]: {
						...agentCategories[categoryName],
						agentsError,
					},
				};

				setState({ agentCategories: updatedAgentCategories });
			},

		getAgentCategoriesByCategory:
			(category: BrowseCategory): ({ getState }: StoreActionApi<{
                /*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
                agentsMap: Record<string, Agent>;
                loadingCategories: Set<BrowseCategory>;
                agentCategories: StoreAgentCategories;
            }>) => StoreAgentCategory =>
			({ getState }) => {
				const state = getState();
				const { agentCategories } = state;
				return agentCategories[category];
			},

		removeAgent:
			(id: string): ({ setState, getState }: StoreActionApi<{
                /*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
                agentsMap: Record<string, Agent>;
                loadingCategories: Set<BrowseCategory>;
                agentCategories: StoreAgentCategories;
            }>) => {
                agent: Agent | undefined;
                categories: BrowseCategory[];
            } =>
			({ setState, getState }) => {
				const { agentsMap, agentCategories } = getState();
				const catsImpacted: Set<BrowseCategory> = new Set();
				let agentToReturn: Agent | undefined;

				const updatedAgentsMap = { ...agentsMap };
				const updatedAgentCategories = { ...agentCategories };

				if (agentsMap[id]) {
					agentToReturn = agentsMap[id];
					delete updatedAgentsMap[id];
				}

				(Object.keys(agentCategories) as BrowseCategory[]).forEach((category) => {
					if (updatedAgentCategories[category]?.agentIds.includes(id)) {
						updatedAgentCategories[category].agentIds = updatedAgentCategories[
							category
						].agentIds.filter((agentId: string) => agentId !== id);
						catsImpacted.add(category);
					}
				});

				setState({ agentsMap: updatedAgentsMap, agentCategories: updatedAgentCategories });

				return {
					agent: agentToReturn,
					categories: Array.from(catsImpacted),
				};
			},

		addAgent:
			(agent: Agent, categories: BrowseCategory[] = ['all-agents']): ({ setState, getState }: StoreActionApi<{
                /*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
                agentsMap: Record<string, Agent>;
                loadingCategories: Set<BrowseCategory>;
                agentCategories: StoreAgentCategories;
            }>) => void =>
			({ setState, getState }) => {
				const { agentsMap, agentCategories } = getState();
				const updatedAgentsMap = { ...agentsMap };
				const updatedAgentCategories = { ...agentCategories };

				categories.forEach((category) => {
					updatedAgentCategories?.[category]?.agentIds.push(agent.id);
				});

				updatedAgentsMap[agent.id] = agent;

				setState({ agentsMap: updatedAgentsMap, agentCategories: updatedAgentCategories });
			},

		updateAgent:
			(id: string, data: Partial<Agent>): ({ setState, getState }: StoreActionApi<{
                /*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
                agentsMap: Record<string, Agent>;
                loadingCategories: Set<BrowseCategory>;
                agentCategories: StoreAgentCategories;
            }>) => {
                id: string;
                identity_account_id?: string | null;
                named_id: string;
                name: string;
                description: string | null;
                system_prompt_template?: string | null;
                behaviour?: string | null;
                creator_type: "SYSTEM" | "CUSTOMER" | "THIRD_PARTY" | "FORGE" | "OOTB";
                creator?: string | null;
                available_plugins: AgentAvailablePlugins;
                visibility?: "PUBLIC" | "PRIVATE" | null;
                is_default: boolean;
                external_files?: ExternalFile[] | null;
                actor_type: "AGENT";
                creator_cloud_id?: string | null;
                follow_up_prompt_template?: string | null;
                plugin_routing_type?: "DEFAULT" | "SKIP" | null;
                user_defined_conversation_starters: string[] | null;
                favourite: boolean;
                deactivated: boolean;
                deactivatedAt?: string;
                knowledge_sources?: {
                    enabled: boolean;
                    sources: AgentKnowledgeSource[];
                } | null;
                action_config?: AgentAction[] | null;
                external_config_reference?: string;
                icon?: string | null;
                authoring_team?: {
                    id: string;
                    profile_url: string;
                    display_name: string;
                    avatar_url?: string;
                } | null;
                is_verified?: boolean | null;
            } =>
			({ setState, getState }) => {
				const { agentsMap } = getState();
				const updatedAgentsMap = { ...agentsMap };
				const prevData = agentsMap[id] ?? {};
				const updatedData = { ...prevData, ...data };

				updatedAgentsMap[id] = updatedData;

				setState({ agentsMap: updatedAgentsMap });

				return updatedData;
			},

		setCategoryLoading:
			(category: BrowseCategory, loading: boolean): ({ setState, getState }: StoreActionApi<{
                /*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
                agentsMap: Record<string, Agent>;
                loadingCategories: Set<BrowseCategory>;
                agentCategories: StoreAgentCategories;
            }>) => void =>
			({ setState, getState }) => {
				const { loadingCategories } = getState();
				const updatedLoadingCategories = new Set(loadingCategories);

				if (loading) {
					updatedLoadingCategories.add(category);
				} else {
					updatedLoadingCategories.delete(category);
				}

				setState({ loadingCategories: updatedLoadingCategories });
			},

		abortCategoryFetch:
			(category: BrowseCategory): ({ getState }: StoreActionApi<{
                /*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
                agentsMap: Record<string, Agent>;
                loadingCategories: Set<BrowseCategory>;
                agentCategories: StoreAgentCategories;
            }>) => void =>
			({ getState }) => {
				const { agentCategories } = getState();
				agentCategories[category].abortController.abort();
					agentCategories[category].abortController = new AbortController();
			},

		getCachedAgentByExternalConfigReference:
			(externalConfigReference: string): ({ getState }: StoreActionApi<{
                /*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
                agentsMap: Record<string, Agent>;
                loadingCategories: Set<BrowseCategory>;
                agentCategories: StoreAgentCategories;
            }>) => Agent | undefined =>
			({ getState }) => {
				const { agentsMap } = getState();
				return Object.values(agentsMap).find(
					(agent) => agent.external_config_reference === externalConfigReference,
				);
			},
	},
});

// exported for testing
export const useAgentsPaginatedStoreActions: HookActionsFunction<BoundActions<{
    /*
    Hash map which is the source of truth for agent data
    agent categories simply hold the agent ids to perform a lookup
    */
    agentsMap: Record<string, Agent>;
    loadingCategories: Set<BrowseCategory>;
    agentCategories: StoreAgentCategories;
}, {
    /**
     * This action populates a specific category with backend data
     * it will store the ids in `agentCategories` and the agents in `agentsMap`
     */
    populateAgentCategories: ({ agentsList, pageInfo, category, }: {
        agentsList: Agent[];
        pageInfo: PageInfo;
        category: BrowseCategory;
    }) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    setAgentCategoriesByCategory: ({ categoryName, agentCategoryData, }: {
        categoryName: BrowseCategory;
        agentCategoryData: StoreAgentCategory;
    }) => ({ getState, setState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    setCategoryError: (categoryName: BrowseCategory, agentsError: Error | undefined) => ({ getState, setState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    getAgentCategoriesByCategory: (category: BrowseCategory) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => StoreAgentCategory;
    removeAgent: (id: string) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => {
        agent: Agent | undefined;
        categories: BrowseCategory[];
    };
    addAgent: (agent: Agent, categories?: BrowseCategory[]) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    updateAgent: (id: string, data: Partial<Agent>) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => {
        id: string;
        identity_account_id?: string | null;
        named_id: string;
        name: string;
        description: string | null;
        system_prompt_template?: string | null;
        behaviour?: string | null;
        creator_type: "SYSTEM" | "CUSTOMER" | "THIRD_PARTY" | "FORGE" | "OOTB";
        creator?: string | null;
        available_plugins: AgentAvailablePlugins;
        visibility?: "PUBLIC" | "PRIVATE" | null;
        is_default: boolean;
        external_files?: ExternalFile[] | null;
        actor_type: "AGENT";
        creator_cloud_id?: string | null;
        follow_up_prompt_template?: string | null;
        plugin_routing_type?: "DEFAULT" | "SKIP" | null;
        user_defined_conversation_starters: string[] | null;
        favourite: boolean;
        deactivated: boolean;
        deactivatedAt?: string;
        knowledge_sources?: {
            enabled: boolean;
            sources: AgentKnowledgeSource[];
        } | null;
        action_config?: AgentAction[] | null;
        external_config_reference?: string;
        icon?: string | null;
        authoring_team?: {
            id: string;
            profile_url: string;
            display_name: string;
            avatar_url?: string;
        } | null;
        is_verified?: boolean | null;
    };
    setCategoryLoading: (category: BrowseCategory, loading: boolean) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    abortCategoryFetch: (category: BrowseCategory) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    getCachedAgentByExternalConfigReference: (externalConfigReference: string) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => Agent | undefined;
}>> = createActionsHook(agentsPaginatedStore);

// exported for testing
export const useAgentsPaginatedByAgentIdStoreHook: HookFunction<{
    agent: Agent;
    doesAgentExist: boolean;
}, BoundActions<{
    /*
    Hash map which is the source of truth for agent data
    agent categories simply hold the agent ids to perform a lookup
    */
    agentsMap: Record<string, Agent>;
    loadingCategories: Set<BrowseCategory>;
    agentCategories: StoreAgentCategories;
}, {
    /**
     * This action populates a specific category with backend data
     * it will store the ids in `agentCategories` and the agents in `agentsMap`
     */
    populateAgentCategories: ({ agentsList, pageInfo, category, }: {
        agentsList: Agent[];
        pageInfo: PageInfo;
        category: BrowseCategory;
    }) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    setAgentCategoriesByCategory: ({ categoryName, agentCategoryData, }: {
        categoryName: BrowseCategory;
        agentCategoryData: StoreAgentCategory;
    }) => ({ getState, setState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    setCategoryError: (categoryName: BrowseCategory, agentsError: Error | undefined) => ({ getState, setState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    getAgentCategoriesByCategory: (category: BrowseCategory) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => StoreAgentCategory;
    removeAgent: (id: string) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => {
        agent: Agent | undefined;
        categories: BrowseCategory[];
    };
    addAgent: (agent: Agent, categories?: BrowseCategory[]) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    updateAgent: (id: string, data: Partial<Agent>) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => {
        id: string;
        identity_account_id?: string | null;
        named_id: string;
        name: string;
        description: string | null;
        system_prompt_template?: string | null;
        behaviour?: string | null;
        creator_type: "SYSTEM" | "CUSTOMER" | "THIRD_PARTY" | "FORGE" | "OOTB";
        creator?: string | null;
        available_plugins: AgentAvailablePlugins;
        visibility?: "PUBLIC" | "PRIVATE" | null;
        is_default: boolean;
        external_files?: ExternalFile[] | null;
        actor_type: "AGENT";
        creator_cloud_id?: string | null;
        follow_up_prompt_template?: string | null;
        plugin_routing_type?: "DEFAULT" | "SKIP" | null;
        user_defined_conversation_starters: string[] | null;
        favourite: boolean;
        deactivated: boolean;
        deactivatedAt?: string;
        knowledge_sources?: {
            enabled: boolean;
            sources: AgentKnowledgeSource[];
        } | null;
        action_config?: AgentAction[] | null;
        external_config_reference?: string;
        icon?: string | null;
        authoring_team?: {
            id: string;
            profile_url: string;
            display_name: string;
            avatar_url?: string;
        } | null;
        is_verified?: boolean | null;
    };
    setCategoryLoading: (category: BrowseCategory, loading: boolean) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    abortCategoryFetch: (category: BrowseCategory) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    getCachedAgentByExternalConfigReference: (externalConfigReference: string) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => Agent | undefined;
}>, string> = createHook(agentsPaginatedStore, {
	// TODO https://product-fabric.atlassian.net/browse/RAGE-618
	// change `{ agentId }` to `agentId`
	selector: (state, agentId: string) => ({
		agent: state.agentsMap[agentId],
		doesAgentExist: Boolean(state.agentsMap[agentId]),
	}),
});

export const useAgentsPaginatedByAgentId: (props: Omit<{
    agentId: string | undefined;
} & AssistanceServiceInjectedParams, "assistanceServiceInjectedParams"> & AssistanceServiceInputParam) => {
    agent: undefined;
} | {
    agent: Agent;
} = createHookWithAssistanceService(
	({
		agentId,
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
	}: {
		agentId: string | undefined;
	} & AssistanceServiceInjectedParams) => {
		di(
			useAgentsPaginatedByAgentId,
			useAgentsPaginatedByAgentIdStoreHook,
			useAgentsPaginatedStoreActions,
		);

		const [{ agent, doesAgentExist }] = useAgentsPaginatedByAgentIdStoreHook(agentId ?? '');
		const { updateAgent } = useAgentsPaginatedStoreActions();

		useQuery({
			queryKey: ['agentHydration', agentId ?? ''],
			queryFn: async (): Promise<AgentDetails> => {
				if (!agentId) {
					// this throw won't happen because the `enabled` field below
					throw new Error('Agent ID is required');
				}

				experienceTracker.start({
					name: ExperienceName.HYDRATE_AGENT_DATA,
					id: ExperienceName.HYDRATE_AGENT_DATA,
				});
				const response = await assistanceService.getAgentDetails(agentId);
				return response;
			},
			enabled: Boolean(agentId) && !doesAgentExist,
			onSuccess: (data) => {
				if (!agentId) {
					return;
				}

				experienceTracker.succeed({
					name: ExperienceName.HYDRATE_AGENT_DATA,
				});

				updateAgent(agentId ?? '', data);
			},
			onError: (error) => {
				if (!agentId) {
					return;
				}

				if (error.errorAttributes.statusCode !== 404) {
					experienceTracker.fail({
						name: ExperienceName.HYDRATE_AGENT_DATA,
						error: error as Error,
						teamName: ROVO_AGENTS_FE_TEAM_NAME,
					});
				}
			},
		});

		if (!agentId) {
			return { agent: undefined };
		}

		return { agent };
	},
);

/**
 * @deprecated This hook has few agent actions calling backend and does optimistic updates
 * Only to be used within this package as the optimistic update does not make sense for other packages
 */
export const useAgentsPaginatedActionsWithOptimistic: (props: Omit<UseAgentsControllerProps, "assistanceServiceInjectedParams"> & AssistanceServiceInputParam) => {
    deleteAgent: {
        data: void | null;
        error: ApiError | null;
        isSubmitting: boolean;
        mutate: (payload: string, callbacks?: {
            onSuccess?: ((data: void) => void) | undefined;
            onError?: (error: ApiError) => void;
        } | undefined) => Promise<void | undefined>;
    };
    setFavouriteAgent: (id: string, favourite: boolean, agentAri?: string | null) => Promise<void | undefined>;
    setVerifiedAgent: (id: string, isVerified: boolean) => void;
    deleteAgentLocally: (id: string) => {
        restore: () => void;
    };
} = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams }: UseAgentsControllerProps) => {
		const environment = useRelayEnvironment();
		const removedAgentCache = useRef<{
			categories: BrowseCategory[];
			agent: Agent | undefined;
		}>({
			categories: [],
			agent: undefined,
		});

		const { removeAgent, addAgent, updateAgent } = useAgentsPaginatedStoreActions();
		const { favouriteAgent, unfavouriteAgent } = useAgentFavourites({
			assistanceServiceParams: assistanceServiceInjectedParams,
		});

		// Note: Experience Tracker events implemented in useDeleteAgent
		const { deleteAgent } = useDeleteAgent({
			assistanceServiceParams: assistanceServiceInjectedParams,
			onInit: (id) => {
				const { categories, agent } = removeAgent(id);
				removedAgentCache.current = { categories, agent };
			},
			onError: () => {
				if (removedAgentCache.current.agent) {
					addAgent(removedAgentCache.current.agent, removedAgentCache.current.categories);
				}
			},
		});

		// Note: Experience Tracker events implemented in useAgentFavourites
		const setFavouriteAgent = useCallback(
			async (id: string, favourite: boolean, agentAri?: string | null) => {
				// Optimistic update
				updateAgent(id, { favourite });

				const localRelayUpdate = async (isFavourite: boolean) => {
					if (agentAri) {
						commitLocalUpdate(environment, (store) => {
							const id = RelayDataID(
								{
									id: agentAri,
								},
								'AgentStudioAssistant',
							);

							if (!id) {
								return;
							}

							const agentRecord = store.get(id);
							if (agentRecord) {
								agentRecord.setValue(isFavourite, 'isFavourite');
							}

							const root = store.getRoot();

							const connection = ConnectionHandler.getConnection(
								root,
								'paginatedAgentsList_agentStudio_getAgents',
								{
									cloudId: assistanceServiceInjectedParams.siteId,
									input: {
										onlyFavouriteAgents: true,
									},
									product: assistanceServiceInjectedParams.product,
								},
							);

							const agentDataID = agentRecord?.getDataID();

							if (!connection || !agentDataID || !agentRecord) {
								return;
							}

							if (isFavourite) {
								const edge = ConnectionHandler.createEdge(
									store,
									connection,
									agentRecord,
									'AgentStudioAgentEdge',
								);

								ConnectionHandler.insertEdgeAfter(connection, edge);
							} else {
								ConnectionHandler.deleteNode(connection, agentDataID);
							}
						});
					}
				};

				localRelayUpdate(favourite);

				const errorCallback = {
					onError: () => {
						// Reverse optimistic update
						updateAgent(id, { favourite: !favourite });

						localRelayUpdate(!favourite);
					},
				};

				if (favourite) {
					return await favouriteAgent.mutate(id, errorCallback);
				} else {
					return await unfavouriteAgent.mutate(id, errorCallback);
				}
			},
			[
				assistanceServiceInjectedParams.product,
				assistanceServiceInjectedParams.siteId,
				environment,
				favouriteAgent,
				unfavouriteAgent,
				updateAgent,
			],
		);

		/**
		 * Function that updates the verified status of an agent.
		 * This can be removed once the dropdown has converted over to relay.
		 */
		const setVerifiedAgent = useCallback(
			(id: string, isVerified: boolean) => {
				updateAgent(id, { is_verified: isVerified });
			},
			[updateAgent],
		);

		// Local cache update function for optimistic delete
		// This only handles cache updates, not API calls or analytics
		const deleteAgentLocally = useCallback(
			(id: string) => {
				// Optimistically remove from cache
				const { categories, agent } = removeAgent(id);

				// Return restore function for error handling
				return {
					restore: () => {
						if (agent) {
							addAgent(agent, categories);
						}
					},
				};
			},
			[removeAgent, addAgent],
		);

		return {
			deleteAgent,
			setFavouriteAgent,
			setVerifiedAgent,
			deleteAgentLocally,
		};
	},
);

export const usePaginatedAgentsByCategory: HookFunction<{
    agentsByCategory: Agent[];
    isCategoryInitialized: boolean;
    isCategoryLoading: boolean;
    isCategoryEmpty: boolean;
    hasNextPage: boolean;
    nextPageCursor: string | null | undefined;
    agentsError: Error | undefined;
}, BoundActions<{
    /*
    Hash map which is the source of truth for agent data
    agent categories simply hold the agent ids to perform a lookup
    */
    agentsMap: Record<string, Agent>;
    loadingCategories: Set<BrowseCategory>;
    agentCategories: StoreAgentCategories;
}, {
    /**
     * This action populates a specific category with backend data
     * it will store the ids in `agentCategories` and the agents in `agentsMap`
     */
    populateAgentCategories: ({ agentsList, pageInfo, category, }: {
        agentsList: Agent[];
        pageInfo: PageInfo;
        category: BrowseCategory;
    }) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    setAgentCategoriesByCategory: ({ categoryName, agentCategoryData, }: {
        categoryName: BrowseCategory;
        agentCategoryData: StoreAgentCategory;
    }) => ({ getState, setState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    setCategoryError: (categoryName: BrowseCategory, agentsError: Error | undefined) => ({ getState, setState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    getAgentCategoriesByCategory: (category: BrowseCategory) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => StoreAgentCategory;
    removeAgent: (id: string) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => {
        agent: Agent | undefined;
        categories: BrowseCategory[];
    };
    addAgent: (agent: Agent, categories?: BrowseCategory[]) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    updateAgent: (id: string, data: Partial<Agent>) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => {
        id: string;
        identity_account_id?: string | null;
        named_id: string;
        name: string;
        description: string | null;
        system_prompt_template?: string | null;
        behaviour?: string | null;
        creator_type: "SYSTEM" | "CUSTOMER" | "THIRD_PARTY" | "FORGE" | "OOTB";
        creator?: string | null;
        available_plugins: AgentAvailablePlugins;
        visibility?: "PUBLIC" | "PRIVATE" | null;
        is_default: boolean;
        external_files?: ExternalFile[] | null;
        actor_type: "AGENT";
        creator_cloud_id?: string | null;
        follow_up_prompt_template?: string | null;
        plugin_routing_type?: "DEFAULT" | "SKIP" | null;
        user_defined_conversation_starters: string[] | null;
        favourite: boolean;
        deactivated: boolean;
        deactivatedAt?: string;
        knowledge_sources?: {
            enabled: boolean;
            sources: AgentKnowledgeSource[];
        } | null;
        action_config?: AgentAction[] | null;
        external_config_reference?: string;
        icon?: string | null;
        authoring_team?: {
            id: string;
            profile_url: string;
            display_name: string;
            avatar_url?: string;
        } | null;
        is_verified?: boolean | null;
    };
    setCategoryLoading: (category: BrowseCategory, loading: boolean) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    abortCategoryFetch: (category: BrowseCategory) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    getCachedAgentByExternalConfigReference: (externalConfigReference: string) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => Agent | undefined;
}>, BrowseCategory> = createHook(agentsPaginatedStore, {
	selector: (state, category: BrowseCategory) => ({
		agentsByCategory: state.agentCategories[category].agentIds.map(
				(agentId) => state.agentsMap[agentId],
			),

			isCategoryInitialized:
			state.agentCategories[category].pageInfo.startCursor !== undefined &&
			state.agentCategories[category].pageInfo.endCursor !== undefined,
			isCategoryLoading: state.loadingCategories.has(category),
		isCategoryEmpty: state.agentCategories[category].agentIds.length === 0,

		hasNextPage: state.agentCategories[category].pageInfo.hasNextPage,
		nextPageCursor: state.agentCategories[category].pageInfo.endCursor,

		agentsError: state.agentCategories[category].agentsError,
	}),
});

// exported for testing only
export const usePaginatedAgentsWithOverrides: (props: Omit<UseAgentsControllerProps, "assistanceServiceInjectedParams"> & AssistanceServiceInputParam) => {
    fetchOverriddenAgentsAndPopulateStore: () => Promise<string[] | null | undefined>;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
	}: UseAgentsControllerProps) => {
		di(useAgentOverrider, useAgentsPaginatedStoreActions);
		const { agentDefaultIdOverride, agentIdFiltersOverride } = useAgentOverrider();
		const { addAgent, populateAgentCategories } = useAgentsPaginatedStoreActions();

		const fetchOverriddenAgentsAndPopulateStoreQuery = useQuery({
			queryKey: [
				'fetchOverriddenAgentsAndPopulateStore',
				agentDefaultIdOverride ?? '',
				agentIdFiltersOverride?.join(',') ?? '',
			],
			queryFn: async () => {
				const agentIdsToFetchSet: Set<string> = new Set();
				if (agentDefaultIdOverride) {
					agentIdsToFetchSet.add(agentDefaultIdOverride);
				}
				if (agentIdFiltersOverride?.length) {
					agentIdFiltersOverride.forEach((agentId) => {
						agentIdsToFetchSet.add(agentId);
					});
				}

				if (agentIdsToFetchSet.size === 0) {
					return;
				}
				const agentIdsToFetch = Array.from(agentIdsToFetchSet) as string[];

				populateAgentCategories({
					category: 'all-agents',
					agentsList: [],
					pageInfo: DEFAULT_PAGE_INFO,
				});

				experienceTracker.start({
					name: ExperienceName.FETCH_AGENTS_OVERRIDES,
					id: ExperienceName.FETCH_AGENTS_OVERRIDES,
				});

				const agents = await Promise.all(
					agentIdsToFetch.map(async (agentId) => {
						return await assistanceService.getAgentDetails(agentId);
					}),
				);

				// then add the agents into "All agents" category
				agents.forEach((agent) => {
					addAgent(agent, ['all-agents']);
				});

				return agentIdsToFetch;
			},
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.FETCH_AGENTS_OVERRIDES,
				});
			},
			onError: (error) => {
				experienceTracker.fail({
					name: ExperienceName.FETCH_AGENTS_OVERRIDES,
					error: error as Error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
			enabled: false,
		});

		return {
			fetchOverriddenAgentsAndPopulateStore: fetchOverriddenAgentsAndPopulateStoreQuery.refetch,
		};
	},
);

export const usePaginatedAgentsActions: (props: Omit<UseAgentsControllerProps, "assistanceServiceInjectedParams"> & AssistanceServiceInputParam) => {
    fetchAgentsByCategory: ({ category, after, query, }: {
        category?: BrowseCategory;
        after?: string | null;
        query?: string;
    }) => Promise<string[]>;
    populateAgentCategories: (args_0: {
        agentsList: Agent[];
        pageInfo: PageInfo;
        category: BrowseCategory;
    }) => void;
    setAgentCategoriesByCategory: (args_0: {
        categoryName: BrowseCategory;
        agentCategoryData: StoreAgentCategory;
    }) => void;
    setCategoryError: (categoryName: BrowseCategory, agentsError: Error | undefined) => void;
    getAgentCategoriesByCategory: (category: BrowseCategory) => StoreAgentCategory;
    removeAgent: (id: string) => {
        agent: Agent | undefined;
        categories: BrowseCategory[];
    };
    addAgent: (agent: Agent, categories?: BrowseCategory[] | undefined) => void;
    updateAgent: (id: string, data: Partial<Agent>) => {
        id: string;
        identity_account_id?: string | null;
        named_id: string;
        name: string;
        description: string | null;
        system_prompt_template?: string | null;
        behaviour?: string | null;
        creator_type: "SYSTEM" | "CUSTOMER" | "THIRD_PARTY" | "FORGE" | "OOTB";
        creator?: string | null;
        available_plugins: AgentAvailablePlugins;
        visibility?: "PUBLIC" | "PRIVATE" | null;
        is_default: boolean;
        external_files?: ExternalFile[] | null;
        actor_type: "AGENT";
        creator_cloud_id?: string | null;
        follow_up_prompt_template?: string | null;
        plugin_routing_type?: "DEFAULT" | "SKIP" | null;
        user_defined_conversation_starters: string[] | null;
        favourite: boolean;
        deactivated: boolean;
        deactivatedAt?: string;
        knowledge_sources?: {
            enabled: boolean;
            sources: AgentKnowledgeSource[];
        } | null;
        action_config?: AgentAction[] | null;
        external_config_reference?: string;
        icon?: string | null;
        authoring_team?: {
            id: string;
            profile_url: string;
            display_name: string;
            avatar_url?: string;
        } | null;
        is_verified?: boolean | null;
    };
    setCategoryLoading: (category: BrowseCategory, loading: boolean) => void;
    abortCategoryFetch: (category: BrowseCategory) => void;
    getCachedAgentByExternalConfigReference: (externalConfigReference: string) => Agent | undefined;
} = createHookWithAssistanceService(
	({ onError, assistanceServiceInjectedParams }: UseAgentsControllerProps) => {
		di(useAgentsPaginatedStoreActions, useAgentOverrider, usePaginatedAgentsWithOverrides);

		const {
			experienceTracker,
			siteId,
			product,
			headers: assistanceServiceHeaders,
		} = assistanceServiceInjectedParams;

		const actions = useAgentsPaginatedStoreActions();
		const {
			setCategoryLoading,
			setCategoryError,
			populateAgentCategories,
			getAgentCategoriesByCategory,
			setAgentCategoriesByCategory,
		} = actions;

		const { agentDefaultIdOverride, agentIdFiltersOverride } = useAgentOverrider();
		const { fetchOverriddenAgentsAndPopulateStore } = usePaginatedAgentsWithOverrides({
			assistanceServiceParams: assistanceServiceInjectedParams,
		});

		const fetchAgentsByCategory = useCallback(
			async ({
				category = 'all-agents',
				after,
				query,
			}: {
				category?: BrowseCategory;
				after?: string | null;
				query?: string;
			}): Promise<string[]> => {
				// if there are any overrides, cancel the normal fetchByCategory and instead fetch the overridden agents
				if (agentDefaultIdOverride || agentIdFiltersOverride?.length) {
					return (await fetchOverriddenAgentsAndPopulateStore()) ?? [];
				}

				const dataBeforeFetch = getAgentCategoriesByCategory(category);

				experienceTracker.start({
					name: ExperienceName.FETCH_AGENTS_PAGINATED,
					id: ExperienceName.FETCH_AGENTS_PAGINATED,
				});

				let queryInput: {
					onlyTemplateAgents?: boolean;
					onlyFavouriteAgents?: boolean;
					onlyMyAgents?: boolean;
					onlyVerifiedAgents?: boolean;
					name?: string;
				} = {};

				switch (category) {
					case 'starred-agents':
						queryInput.onlyFavouriteAgents = true;
						break;
					case 'my-agents':
						queryInput.onlyMyAgents = true;
						break;
					case 'templates':
						queryInput.onlyTemplateAgents = true;
						break;
					case 'verified-agents':
						queryInput.onlyVerifiedAgents = true;
						break;
				}

				if (query) {
					queryInput.name = query;
				}

				// for rolling back data in case of abort/failure
				const resetData = (): string[] => {
					setAgentCategoriesByCategory({
						categoryName: category,
						agentCategoryData: dataBeforeFetch,
					});

					return dataBeforeFetch.agentIds;
				};

				// No after means we're requesting the first page so the category data is reset
				if (!after) {
					populateAgentCategories({
						category,
						agentsList: [],
						pageInfo: DEFAULT_PAGE_INFO,
					});
				}

				setCategoryLoading(category, true);
				setCategoryError(category, undefined);

				const headers = new Headers(assistanceServiceHeaders);
				const maybeWorkspaceId = headers.get('x-workspace-id');

				try {
					const response = await fetchPaginatedAgents({
						after: after ?? undefined,
						cloudId: siteId,
						count: 10,
						input: Object.keys(queryInput).length > 0 ? queryInput : undefined,
						product: product,
						abortController: getAgentCategoriesByCategory(category).abortController,
						workspaceId: maybeWorkspaceId,
					});

					setCategoryLoading(category, false);

					if (getAgentCategoriesByCategory(category).abortController.signal?.aborted) {
						experienceTracker.abort({
							reason: 'Aborted',
							name: ExperienceName.FETCH_AGENTS_PAGINATED,
						});
						return resetData();
					}

					if (response) {
						populateAgentCategories({
							category,
							agentsList: response.agents,
							pageInfo: response.pageInfo,
						});

						experienceTracker.succeed({
							name: ExperienceName.FETCH_AGENTS_PAGINATED,
						});
					}

					return response?.agents.map((agent) => agent.id);
				} catch (error) {
					experienceTracker.fail({
						name: ExperienceName.FETCH_AGENTS_PAGINATED,
						error: error as Error,
						teamName: ROVO_AGENTS_FE_TEAM_NAME,
					});

					const prevAgentIds = resetData();
					setCategoryError(category, error as Error);
					setCategoryLoading(category, false);

					if (error instanceof ApiError) {
						onError?.(error);
					}

					return prevAgentIds;
				}
			},
			[
				agentDefaultIdOverride,
				agentIdFiltersOverride?.length,
				getAgentCategoriesByCategory,
				experienceTracker,
				setCategoryLoading,
				setCategoryError,
				assistanceServiceHeaders,
				fetchOverriddenAgentsAndPopulateStore,
				setAgentCategoriesByCategory,
				populateAgentCategories,
				siteId,
				product,
				onError,
			],
		);

		return {
			...actions,
			fetchAgentsByCategory,
		};
	},
);

// exported for testing only
export const usePaginatedAgentsMap: HookFunction<Record<string, Agent>, BoundActions<{
    /*
    Hash map which is the source of truth for agent data
    agent categories simply hold the agent ids to perform a lookup
    */
    agentsMap: Record<string, Agent>;
    loadingCategories: Set<BrowseCategory>;
    agentCategories: StoreAgentCategories;
}, {
    /**
     * This action populates a specific category with backend data
     * it will store the ids in `agentCategories` and the agents in `agentsMap`
     */
    populateAgentCategories: ({ agentsList, pageInfo, category, }: {
        agentsList: Agent[];
        pageInfo: PageInfo;
        category: BrowseCategory;
    }) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    setAgentCategoriesByCategory: ({ categoryName, agentCategoryData, }: {
        categoryName: BrowseCategory;
        agentCategoryData: StoreAgentCategory;
    }) => ({ getState, setState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    setCategoryError: (categoryName: BrowseCategory, agentsError: Error | undefined) => ({ getState, setState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    getAgentCategoriesByCategory: (category: BrowseCategory) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => StoreAgentCategory;
    removeAgent: (id: string) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => {
        agent: Agent | undefined;
        categories: BrowseCategory[];
    };
    addAgent: (agent: Agent, categories?: BrowseCategory[]) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    updateAgent: (id: string, data: Partial<Agent>) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => {
        id: string;
        identity_account_id?: string | null;
        named_id: string;
        name: string;
        description: string | null;
        system_prompt_template?: string | null;
        behaviour?: string | null;
        creator_type: "SYSTEM" | "CUSTOMER" | "THIRD_PARTY" | "FORGE" | "OOTB";
        creator?: string | null;
        available_plugins: AgentAvailablePlugins;
        visibility?: "PUBLIC" | "PRIVATE" | null;
        is_default: boolean;
        external_files?: ExternalFile[] | null;
        actor_type: "AGENT";
        creator_cloud_id?: string | null;
        follow_up_prompt_template?: string | null;
        plugin_routing_type?: "DEFAULT" | "SKIP" | null;
        user_defined_conversation_starters: string[] | null;
        favourite: boolean;
        deactivated: boolean;
        deactivatedAt?: string;
        knowledge_sources?: {
            enabled: boolean;
            sources: AgentKnowledgeSource[];
        } | null;
        action_config?: AgentAction[] | null;
        external_config_reference?: string;
        icon?: string | null;
        authoring_team?: {
            id: string;
            profile_url: string;
            display_name: string;
            avatar_url?: string;
        } | null;
        is_verified?: boolean | null;
    };
    setCategoryLoading: (category: BrowseCategory, loading: boolean) => ({ setState, getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    abortCategoryFetch: (category: BrowseCategory) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => void;
    getCachedAgentByExternalConfigReference: (externalConfigReference: string) => ({ getState }: StoreActionApi<{
        /*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
        agentsMap: Record<string, Agent>;
        loadingCategories: Set<BrowseCategory>;
        agentCategories: StoreAgentCategories;
    }>) => Agent | undefined;
}>, void> = createHook(agentsPaginatedStore, {
	selector: (state) => state.agentsMap,
});

export const useRecommendedAgents: (props: Omit<AssistanceServiceInjectedParams & {
    query?: string; // deprecated: to be removed in QRA-485
    limit?: number;
    pageUrl?: string;
}, "assistanceServiceInjectedParams"> & AssistanceServiceInputParam) => {
    isFetching: boolean;
    recommendedAgents: Agent[];
    recommendationKey?: string | undefined;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams,
		query,
		limit = 3,
		pageUrl,
	}: AssistanceServiceInjectedParams & {
		query?: string; // deprecated: to be removed in QRA-485
		limit?: number;
		pageUrl?: string;
	}): {
		isFetching: boolean;
		recommendedAgents: Agent[];
		recommendationKey?: string | undefined;
	} => {
		di(usePaginatedAgentsMap);
		const { assistanceService, experienceTracker, siteId } = assistanceServiceInjectedParams;
		const [agentsMap, { updateAgent }] = usePaginatedAgentsMap();

		const { isFetching, data } = useQuery({
			queryKey: ['recommendedAgents', query ?? '', limit, pageUrl ?? ''],
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.GET_RECOMMENDED_AGENTS,
					name: ExperienceName.GET_RECOMMENDED_AGENTS,
				});

				const response = await assistanceService.getRecommendedAgents({
					limit,
					query: query ?? '',
					pageUrl,
				} as { limit: number; query: string; pageUrl?: string });

				// store the agents in the paginated store for optimistic updates etc
				// not adding them to specific category in local cache on purpose
				response.agents.forEach((agent) => {
					updateAgent(agent.id, agent);
				});

				return response;
			},
			enabled: Boolean(siteId),
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.GET_RECOMMENDED_AGENTS,
				});
			},
			onError: (error) => {
				if (isHipaaError(error)) {
					experienceTracker.succeed({
						name: ExperienceName.GET_RECOMMENDED_AGENTS,
					});
					return;
				}
				experienceTracker.fail({
					name: ExperienceName.GET_RECOMMENDED_AGENTS,
					error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
		});

		return {
			isFetching,
			recommendedAgents: data?.agents.map((agent) => agentsMap[agent.id]).filter(Boolean) ?? [],
			recommendationKey: data?.metadata?.recommendationKey,
		};
	},
);

export const usePaginatedAgentByConfigReference: (props: Omit<UseAgentsControllerProps, "assistanceServiceInjectedParams"> & AssistanceServiceInputParam) => {
    getAndStoreAgentByExternalConfigReference: ({ externalConfigReference }: { externalConfigReference: string; }) => Promise<Agent | undefined>;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { experienceTracker, siteId },
	}: UseAgentsControllerProps) => {
		di(useAgentsPaginatedStoreActions, fetchAgentByExternalConfigReference);

		const { updateAgent, getCachedAgentByExternalConfigReference } =
			useAgentsPaginatedStoreActions();

		const getAndStoreAgentByExternalConfigReference = useCallback(
			async ({ externalConfigReference }: { externalConfigReference: string }) => {
				const cachedAgent = getCachedAgentByExternalConfigReference(externalConfigReference);
				if (cachedAgent) {
					return cachedAgent;
				}

				try {
					experienceTracker.start({
						name: ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE,
						id: ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE,
					});

					const agentData = await fetchAgentByExternalConfigReference({
						cloudId: siteId,
						externalConfigReference,
					});

					// cache the data
					updateAgent(agentData.id, agentData);

					experienceTracker.succeed({
						name: ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE,
					});

					return agentData;
				} catch (error) {
					experienceTracker.fail({
						name: ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE,
						error: error as Error,
						teamName: ROVO_AGENTS_FE_TEAM_NAME,
					});

					return undefined;
				}
			},
			[getCachedAgentByExternalConfigReference, experienceTracker, siteId, updateAgent],
		);

		return {
			getAndStoreAgentByExternalConfigReference,
		};
	},
);

/**
 * @deprecated Please use `usePaginatedAgents...` hooks instead
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
export const useAgentsController: typeof useAgentsControllerInternal = (param) => {
	return useAgentsControllerInternal(param);
};

/**
 * Use useAgentsList in contexts in which we want to display at least three default favourited agents, such as in the editor
 * @deprecated Please use `usePaginatedAgents...` hooks instead
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
export const useAgentsList: (props: Omit<UseAgentsListProps, "assistanceServiceInjectedParams"> & AssistanceServiceInputParam) => Agent[] = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { product, experienceId, headers, touchpointSource, siteId },
		filter,
	}: UseAgentsListProps) => {
		const { availableAgents, favouritedAgents } = useAgentsController({
			assistanceServiceParams: {
				product,
				experienceId,
				headers,
				touchpointSource,
				siteId,
			},
		});

		const defaultAgents = getSuggestedOotbAgents({ availableAgents, product });

		const defaultAgentsOutsideUserFavourites = defaultAgents.filter(
			(defaultAgent) => !favouritedAgents.find((favAgent) => favAgent.id === defaultAgent.id),
		);

		const favouritedAgentsWithDefault = [
			...favouritedAgents,
			...defaultAgentsOutsideUserFavourites.slice(0, Math.max(3 - favouritedAgents.length, 0)),
		];

		return filter === 'favourites' ? favouritedAgentsWithDefault : availableAgents;
	},
);
